import React, { useContext, useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Grid,
    IconButton,
    Collapse,
    useMediaQuery,
    Select,
    MenuItem,
    SelectChangeEvent
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { AppContext } from "../context/AppContext";
import { useGuestLabel } from "../hooks/useGuestLabel";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { getBrandLogo } from "../styles/branding";
import BrandingThemeToggle from "./BrandingThemeToggle";
import { useBrandAppearance } from "../hooks/useBrandAppearance";

const Navbar: React.FC = () => {
    const theme = useTheme();
    const { colors: BrandColors, isDark: darkUi } = useBrandAppearance();
    const logo = getBrandLogo(darkUi ? "dark" : "light");
    const { state } = useContext(AppContext) ?? {};
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [showDetails, setShowDetails] = useState(false);
    const { t, i18n } = useTranslation("navbar");
    const { hotel, arrivalDate, departureDate, roomType } = state;
    const { adultCount, childCount } = state;

    const getGuestLabel = useGuestLabel("navbar");
    const { changeLanguage } = useNavigateWithLang();
    
    const handleLanguageChange = (event: SelectChangeEvent<string>) => {
        changeLanguage(event.target.value);
    };

    const labelColor = darkUi ? "rgba(255,255,255,0.8)" : "var(--oc-text-muted)";
    const bodyColor = darkUi ? "#f0f0f0" : "var(--oc-text)";

    return (
        <AppBar
            elevation={0}
            sx={{
                backgroundColor: "var(--oc-navbar-bg)",
                borderBottom: "0px solid #E0E0E0",
            }}
            position="sticky"
        >
            <Toolbar
                sx={{
                    minHeight: isMobile ? "80px !important" : "110px !important",
                    px: { xs: "0 !important", md: "0 !important" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "stretch",
                }}
            >
                {/* Logo Box - stays on left for all breakpoints */}
                <Box
                    sx={{
                        backgroundColor: BrandColors.primary,
                        width: { xs: 120, md: 190 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {/* <img
                        src={SCLogo}
                        alt=""
                        style={{
                            height: isMobile ? "45px" : "70px",
                            width: "auto",
                        }}
                    /> */}
                    <img
                        src={logo}
                        alt=""
                        style={{
                            height: isMobile ? "45px" : "70px",
                            width: "auto",
                        }}
                    />
                </Box>

                {/* Right section */}
                {isMobile ? (
                    <Box
                        sx={{
                            backgroundColor: BrandColors.primary,
                            color: "#000000",
                            flexGrow: 1,
                            display: "flex",
                            justifyContent: "end",
                            alignItems: "center",
                            pl: 2,
                            gap: 0.5,
                        }}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center" }}>
                            <BrandingThemeToggle variant="onDark" />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "white" }}>
                        {t("reservationDetails")}
                        </Typography>
                        <IconButton sx={{ color: "white" }}>
                            {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ flexGrow: 1, px: 5, alignItems: "center" }}>
                        <Grid item md={12}>
                            <Typography variant="subtitle2" sx={{ color: labelColor, fontWeight: "bold" }}>{t("hotel")}</Typography>
                            <Typography variant="body1" sx={{ color: bodyColor }}>{hotel}</Typography>
                        </Grid>
                        <Grid container item md={12} spacing={3} sx={{ flexWrap: "nowrap", alignItems: "center" }}>
                            <Grid item md={3}>
                                <Typography variant="subtitle2" sx={{ color: labelColor, fontWeight: "bold" }}>{t("arrival")}</Typography>
                                <Typography variant="body1" sx={{ color: bodyColor }}>{arrivalDate}</Typography>
                            </Grid>
                            <Grid item md={3}>
                                <Typography variant="subtitle2" sx={{ color: labelColor, fontWeight: "bold" }}>{t("departure")}</Typography>
                                <Typography variant="body1" sx={{ color: bodyColor }}>{departureDate}</Typography>
                            </Grid>
                            <Grid item md={3}>
                                <Typography variant="subtitle2" sx={{ color: labelColor, fontWeight: "bold" }}>{t("roomType")}</Typography>
                                <Typography variant="body1" sx={{ color: bodyColor }}>{roomType}</Typography>
                            </Grid>
                            <Grid item md={3}>
                                <Typography variant="subtitle2" sx={{ color: labelColor, fontWeight: "bold" }}>{t("guests.label")}</Typography>
                                <Typography variant="body1" sx={{ color: bodyColor }}>{getGuestLabel(adultCount, childCount)}</Typography>
                            </Grid>
                            <Grid item md={1} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                                <BrandingThemeToggle variant={darkUi ? "onDark" : "onLight"} />
                                <Select
                                    value={i18n.language}
                                    onChange={handleLanguageChange}
                                    variant="standard"
                                    sx={{
                                        fontSize: "1rem",
                                        minWidth: "60px",
                                        backgroundColor: "transparent",
                                        color: darkUi ? "#fff" : undefined,
                                        "& .MuiSelect-select": {
                                            padding: "2px 6px",
                                        },
                                        "&:before, &:after": {
                                            display: "none",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "1rem",
                                            color: darkUi ? "#fff" : undefined,
                                        },
                                    }}
                                >
                                    <MenuItem value="en">EN</MenuItem>
                                    <MenuItem value="de">DE</MenuItem>
                                    <MenuItem value="hr">HR</MenuItem>
                                    <MenuItem value="it">IT</MenuItem>
                                    <MenuItem value="es">ES</MenuItem>
                                    <MenuItem value="fr">FR</MenuItem>
                                </Select>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Toolbar>

            {/* Collapsible content only visible when open on mobile */}
            {isMobile && (
                <Collapse in={showDetails} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, backgroundColor: darkUi ? "rgba(0,0,0,0.2)" : "var(--oc-footer-bg)" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: labelColor }}>{t("hotel")}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold", color: bodyColor ?? "#000" }}>{hotel}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold", color: labelColor }}>{t("arrival")}</Typography>
                        <Typography variant="body1" sx={{ color: bodyColor }}>{arrivalDate}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold", color: labelColor }}>{t("departure")}</Typography>
                        <Typography variant="body1" sx={{ color: bodyColor }}>{departureDate}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold", color: labelColor }}>{t("roomType")}</Typography>
                        <Typography variant="body1" sx={{ color: bodyColor }}>{roomType}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold", color: labelColor }}>{t("guests.label")}</Typography>
                        <Typography variant="body1" sx={{ color: bodyColor }}>{getGuestLabel(adultCount, childCount)}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold", color: labelColor }}>{t("accommodationType")}</Typography>
                        <Typography variant="body1" sx={{ color: bodyColor }}>{t("hotel")}</Typography>
                    </Box>
                </Collapse>
            )}
        </AppBar>
    );
};

export default Navbar;